import React, { Dispatch, FC, SetStateAction, useRef, useState } from "react"
import { JSONSchema7 } from "json-schema"
import Form from "@rjsf/core"
import { isValid, SchemaForm } from "resha"

import { DataFieldType } from "../../../../../../Models/DataProgram/DocumentExtraction"
import { useUrlSignerFromContext } from "../../../../../../Hooks/useUrlSigner"
import { randID } from "../../../../../../Helpers/Functions/random"
import { SchemaFormModal } from "../../../Modals"
import { MultiSchemaField } from "../../../Fields"
import { advancedCustomUiSchema, uiSchema } from "./uiSchema"

export enum FieldModalType {
    New = "new",
    Edit = "edit"
}

export type FieldModalChildProps = {
    isValid: boolean
    defaultValue?: DataFieldType
    formData: Partial<DataFieldType>
    updateFormData: (data: unknown) => void
    setValidation: Dispatch<SetStateAction<(() => void) | undefined>>
    setValid: Dispatch<SetStateAction<boolean | undefined>>
}

type FieldModalProps = {
    onClose: () => void
    onSubmit: (newField: DataFieldType) => void
    schema: JSONSchema7
    docType?: string
    index: number
    value?: DataFieldType
    type?: FieldModalType
    mode?: "modal" | "paper"
    children?: FC<FieldModalChildProps>
}

export const FieldModal = ({
    onClose,
    onSubmit,
    schema,
    docType,
    value,
    type = FieldModalType.New,
    mode = "modal",
    children
}: FieldModalProps) => {
    const urlSignerFromContext = useUrlSignerFromContext()

    const formRef = useRef<Form<unknown>>(null)
    const model = schema.definitions
        ?.AnnotationFieldParameterModel as JSONSchema7
    const defaultValue = Object.keys(model.properties as DataFieldType).reduce(
        (properties, property) => {
            return {
                ...properties,
                [property]: (model?.properties?.[property] as JSONSchema7)
                    ?.default || { editable: true }
            }
        },
        {}
    ) as DataFieldType
    const documentSchema = schema.definitions
        ?.AnnotationFieldParameterModel as JSONSchema7

    const [state, setState] = useState<DataFieldType>(
        value ? value : defaultValue
    )

    const [isChildValid, setValid] = useState<boolean>()
    const [validateChild, setChildValidation] = useState<() => void>()

    const triggerSubmit = () => {
        formRef.current?.submit()
        if (validateChild) {
            validateChild()
        }
    }

    const handleChange = (data: unknown) => {
        const value = data as DataFieldType
        setState({
            ...value,
            fieldId: {
                ...value.fieldId,
                data: value.fieldId.data?.trim() ? value.fieldId.data : randID()
            }
        })
    }

    const handleSubmit = (data: unknown) => {
        const requireChildValidation = validateChild ? !isChildValid : false
        if (requireChildValidation) {
            return
        }
        onSubmit(data as DataFieldType)
        onClose()
    }

    const isAdvancedCustom = docType?.includes("Advanced Custom")
    const fieldType = state?.options?.data?.type
    const isTableField = fieldType === "Table"
    const customUiSchema =
        isAdvancedCustom && (fieldType === undefined || isTableField)
            ? advancedCustomUiSchema
            : undefined

    return (
        <SchemaFormModal
            testId="field-modal"
            mode={mode}
            title={
                type === FieldModalType.Edit ? "Edit field" : "Add new field"
            }
            onClose={onClose}
            onSubmit={triggerSubmit}
            submit={{
                label:
                    type === FieldModalType.Edit ? "Update field" : "Add field"
            }}
        >
            <SchemaForm
                ref={formRef}
                schema={{
                    definitions: schema.definitions,
                    ...documentSchema
                }}
                additionalFields={{
                    OneOfField: MultiSchemaField
                }}
                formData={state}
                uiSchema={{
                    ...uiSchema,
                    ...customUiSchema
                }}
                onSubmit={handleSubmit}
                onChange={handleChange}
                enableImageTools={false}
                skipMissingDataField={false}
                urlSigner={urlSignerFromContext}
            />
            {children ? (
                React.createElement(children, {
                    isValid: isValid(
                        {
                            definitions: schema.definitions,
                            ...documentSchema
                        },
                        state,
                        schema
                    ) as boolean,
                    defaultValue: value,
                    formData: state,
                    updateFormData: handleChange,
                    setValidation: setChildValidation,
                    setValid
                })
            ) : (
                <></>
            )}
        </SchemaFormModal>
    )
}
